import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Upload, Eye, EyeOff, Star, Trash2 } from 'lucide-react';

const ActivityCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activities, setActivities] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('token') || '');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const emojiOptions = ['👀', '🫂', '💆‍♂️', '👄', '🍆', '👌', '👅', '🍑', '💦', '😈', '💕', '✨'];

  // 0 = hidden, 1 = blurred emoji, 2 = clear emoji, 3 = emoji+text (media blurred), 4 = full reveal
  const sharingLevels = [
    { value: 0, label: "Don't share", hint: 'Partner sees nothing for this day' },
    { value: 1, label: 'Something happened', hint: 'Partner sees a generic marker that something happened, nothing about what' },
    { value: 2, label: 'Clear emoji', hint: 'Partner sees the emoji clearly, nothing else' },
    { value: 3, label: 'Story (media blurred)', hint: 'Partner sees emoji + full description & notes; any media stays blurred' },
    { value: 4, label: 'Full reveal', hint: 'Partner sees everything, including media, unblurred' },
  ];

  const [formData, setFormData] = useState({
    emojiArray: [],
    description: '',
    notes: '',
    ejaculationCount: 0,
    rating: 0,
    sharingLevel: 0,
    mediaUrl: null,
    mediaFile: null,
    mediaType: null,
  });

  const [partnerModalDate, setPartnerModalDate] = useState(null);
  const [points, setPoints] = useState(null);

  useEffect(() => {
    if (token) {
      setIsLoggedIn(true);
      fetchActivities();
      fetchPoints();
    }
  }, [token]);

  const fetchPoints = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/points`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      setPoints(data);
    } catch (err) {
      console.error('Failed to fetch points:', err);
    }
  };

  const fetchActivities = async (authToken = token) => {
    if (!authToken) return;

    try {
      const response = await fetch(`${API_URL}/api/activities`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
        }
        return;
      }

      const data = await response.json();
      console.log('Fetched activities:', data);
      setActivities(data);
    } catch (err) {
      console.error('Failed to fetch activities:', err);
      setError('Failed to load activities');
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = isRegistering ? '/api/auth/register' : '/api/auth/login';
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Authentication failed');
        setLoading(false);
        return;
      }

      const data = await response.json();
      localStorage.setItem('token', data.token);
      localStorage.setItem('email', email);
      setToken(data.token);
      setIsLoggedIn(true);
      setError('');
    } catch (err) {
      setError('Connection failed. Check your server URL.');
      console.error(err);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    setIsLoggedIn(false);
    setEmail('');
    setPassword('');
    setToken('');
    setActivities({});
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const openDateModal = (day) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(date);
    const key = formatDateKey(date);
    const existing = activities[key]?.you;
    setFormData({
      emojiArray: existing?.emojiArray || [],
      description: existing?.description || '',
      notes: existing?.notes || '',
      ejaculationCount: existing?.ejaculationCount || 0,
      rating: existing?.rating || 0,
      sharingLevel: existing?.sharingLevel || 0,
      mediaUrl: existing?.mediaUrl || null,
      mediaFile: null,
      mediaType: existing?.mediaType || null,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDate(null);
  };

  const handleSaveActivity = async () => {
    if (!selectedDate || !token) return;

    setLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('activityDate', formatDateKey(selectedDate));
      formDataToSend.append('emojiArray', JSON.stringify(formData.emojiArray));
      formDataToSend.append('description', formData.description);
      formDataToSend.append('notes', formData.notes);
      formDataToSend.append('ejaculationCount', formData.ejaculationCount);
      formDataToSend.append('rating', formData.rating);
      formDataToSend.append('sharingLevel', formData.sharingLevel);
      formDataToSend.append('mediaType', formData.mediaType);

      if (formData.mediaFile) {
        formDataToSend.append('media', formData.mediaFile);
      }

      const response = await fetch(`${API_URL}/api/activities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error('Failed to save activity');
      }

      await fetchActivities();
      await fetchPoints();
      closeModal();
    } catch (err) {
      setError('Failed to save activity');
      console.error(err);
    }
    setLoading(false);
  };

  const handleMediaUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const isAudio = file.type.startsWith('audio/');
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData({
          ...formData,
          mediaUrl: event.target.result,
          mediaFile: file,
          mediaType: isImage ? 'image' : isVideo ? 'video' : isAudio ? 'audio' : 'unknown',
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteActivity = async () => {
    if (!selectedDate || !token) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/activities/${formatDateKey(selectedDate)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete activity');
      }

      await fetchActivities();
      await fetchPoints();
      closeModal();
    } catch (err) {
      setError('Failed to delete activity');
      console.error(err);
    }
    setLoading(false);
  };

  const toggleEmoji = (emoji) => {
    if (formData.emojiArray.includes(emoji)) {
      setFormData({
        ...formData,
        emojiArray: formData.emojiArray.filter(e => e !== emoji),
      });
    } else {
      setFormData({
        ...formData,
        emojiArray: [...formData.emojiArray, emoji],
      });
    }
  };

  const renderRating = (rating, onRate) => {
    return (
      <div style={styles.ratingContainer}>
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            onClick={() => onRate(rating === star ? 0 : star)}
            style={{
              ...styles.starButton,
              color: rating >= star ? '#f39c12' : '#ecf0f1',
            }}
          >
            <Star size={20} fill={rating >= star ? '#f39c12' : 'none'} />
          </button>
        ))}
      </div>
    );
  };

  if (!isLoggedIn) {
    return (
      <div style={styles.container}>
        <div style={styles.loginContainer}>
          <h1 style={styles.title}>Activity Calendar</h1>
          <p style={styles.subtitle}>Shared tracking for couples</p>
          
          <form onSubmit={handleAuth} style={styles.loginForm}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  style={styles.input}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.eyeButton}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && <p style={styles.error}>{error}</p>}

            <button type="submit" style={styles.buttonPrimary} disabled={loading}>
              {loading ? 'Loading...' : (isRegistering ? 'Create Account' : 'Login')}
            </button>
          </form>

          <button
            onClick={() => {
              setIsRegistering(!isRegistering);
              setError('');
            }}
            style={styles.toggleLink}
          >
            {isRegistering ? 'Already have an account? Login' : 'Need an account? Register'}
          </button>
        </div>
      </div>
    );
  }

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = [];

  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const displayEmail = localStorage.getItem('email') || email;

  return (
    <div style={styles.container}>
      <style>{`
        * {
          box-sizing: border-box;
        }
        @media (max-width: 768px) {
          .calendar-grid {
            grid-template-columns: 1fr !important;
          }
          .calendar-day {
            aspect-ratio: auto !important;
            min-height: 120px !important;
          }
          .weekdays {
            display: none !important;
          }
          .empty-day {
            display: none !important;
          }
          .modal-overlay {
            padding: 10px !important;
          }
          .modal-content {
            width: calc(100vw - 40px) !important;
            max-width: none !important;
            padding: 15px !important;
          }
        }
      `}</style>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Activity Calendar</h1>
          <p style={styles.userEmail}>Logged in as: {displayEmail}</p>
        </div>
        {points && (
          <div style={styles.pointsBoard}>
            <div style={styles.pointsCard}>
              <div style={styles.pointsLabel}>You</div>
              <div style={styles.pointsTotal}>{points.you.total} pts</div>
              <div style={styles.pointsSub}>{points.you.thisMonth} this month</div>
            </div>
            <div style={styles.pointsCard}>
              <div style={styles.pointsLabel}>Partner</div>
              <div style={styles.pointsTotal}>{points.partner.total} pts</div>
              <div style={styles.pointsSub}>{points.partner.thisMonth} this month</div>
            </div>
          </div>
        )}
        <button onClick={handleLogout} style={styles.buttonSecondary}>
          Logout
        </button>
      </div>

      <div style={styles.calendarWrapper}>
        <div style={styles.monthHeader}>
          <button onClick={handlePrevMonth} style={styles.navButton}>
            <ChevronLeft size={20} />
          </button>
          <h2 style={styles.monthTitle}>{monthName}</h2>
          <button onClick={handleNextMonth} style={styles.navButton}>
            <ChevronRight size={20} />
          </button>
        </div>

        <div style={styles.weekdays} className="weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} style={styles.weekday}>{day}</div>
          ))}
        </div>

        <div style={styles.calendarGrid} className="calendar-grid">
          {days.map((day, idx) => {
            if (day === null) {
              return <div key={`empty-${idx}`} className="empty-day" style={styles.emptyDay}></div>;
            }

            const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            const key = formatDateKey(date);
            const dayActivities = activities[key];
            const today = new Date();
            const isToday = day === today.getDate() && 
                            currentDate.getMonth() === today.getMonth() && 
                            currentDate.getFullYear() === today.getFullYear();

            return (
              <div
                key={key}
                onClick={() => openDateModal(day)}
                className="calendar-day"
                style={{
                  ...styles.calendarDay,
                  ...(isToday ? styles.calendarDayToday : {}),
                  ...(dayActivities ? styles.calendarDayActive : {}),
                }}
              >
                <span style={styles.dayNumber}>{day}</span>
                {dayActivities && (
                  <div style={styles.dayContent}>
                    {dayActivities.you && (
                      <div style={styles.userActivity}>
                        <div style={styles.label}>You</div>
                        <div style={styles.dayEmoji}>
                          {dayActivities.you.emojiArray.join('')}
                        </div>
                        {dayActivities.you.ejaculationCount > 0 && (
                          <div style={styles.indicator}>💦x{dayActivities.you.ejaculationCount}</div>
                        )}
                      </div>
                    )}
                    {dayActivities.partner && (
                      <div
                        style={styles.userActivity}
                        onClick={(e) => {
                          if (dayActivities.partner.sharingLevel > 0) {
                            e.stopPropagation();
                            setPartnerModalDate(key);
                          }
                        }}
                      >
                        <div style={styles.label}>Partner</div>
                       <div style={styles.dayEmoji}>
                          {dayActivities.partner.sharingLevel === 1
                            ? '🔥'
                            : dayActivities.partner.sharingLevel > 1
                              ? dayActivities.partner.emojiArray.join('')
                              : '?'}
                        </div>
                        {dayActivities.partner.sharingLevel >= 3 && dayActivities.partner.ejaculationCount > 0 && (
                          <div style={styles.indicator}>💦x{dayActivities.partner.ejaculationCount}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showModal && selectedDate && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent} className="modal-content">
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </h3>
              <button onClick={closeModal} style={styles.closeButton}>
                <X size={24} />
              </button>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Activity Emojis</label>
              <div style={styles.emojiGrid}>
                {emojiOptions.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => toggleEmoji(emoji)}
                    style={{
                      ...styles.emojiOption,
                      ...(formData.emojiArray.includes(emoji) ? styles.emojiOptionSelected : {}),
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
              <div style={styles.selectedEmojis}>
                Selected: {formData.emojiArray.join(' ')}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Ejaculation Count</label>
              <input
                type="number"
                min="0"
                value={formData.ejaculationCount}
                onChange={(e) => setFormData({ ...formData, ejaculationCount: parseInt(e.target.value) || 0 })}
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Rating</label>
              {renderRating(formData.rating, (rating) => setFormData({ ...formData, rating }))}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Brief Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Morning session"
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional details..."
                style={styles.textarea}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Share with partner</label>
              <div style={styles.sharingLevelGrid}>
                {sharingLevels.map((lvl) => (
                  <button
                    key={lvl.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, sharingLevel: lvl.value })}
                    style={{
                      ...styles.sharingLevelOption,
                      ...(formData.sharingLevel === lvl.value ? styles.sharingLevelOptionSelected : {}),
                    }}
                  >
                    {lvl.label}
                  </button>
                ))}
              </div>
              <p style={styles.hint}>
                {sharingLevels.find(l => l.value === formData.sharingLevel)?.hint}
              </p>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Add Photo, Video, or Audio</label>
              <div style={styles.uploadArea}>
                <input
                  type="file"
                  accept="image/*,video/*,audio/*"
                  onChange={handleMediaUpload}
                  style={styles.fileInput}
                />
                <span style={styles.uploadText}>
                  <Upload size={20} /> Click to upload
                </span>
              </div>
              {formData.mediaUrl && (
                <div style={styles.mediaPreview}>
                  {formData.mediaType === 'image' && (
                    <img src={formData.mediaUrl} alt="Preview" style={styles.previewImage} />
                  )}
                  {formData.mediaType === 'video' && (
                    <video src={formData.mediaUrl} style={styles.previewVideo} controls />
                  )}
                  {formData.mediaType === 'audio' && (
                    <audio src={formData.mediaUrl} style={styles.previewAudio} controls />
                  )}
                  <button
                    onClick={() => setFormData({ ...formData, mediaUrl: null, mediaFile: null, mediaType: null })}
                    style={styles.buttonRemove}
                  >
                    Remove Media
                  </button>
                </div>
              )}
            </div>

            {error && <p style={styles.error}>{error}</p>}

            <div style={styles.modalButtons}>
              {activities[formatDateKey(selectedDate)]?.you && (
                <button onClick={handleDeleteActivity} style={styles.buttonDanger} disabled={loading}>
                  <Trash2 size={16} /> Delete
                </button>
              )}
              <button onClick={closeModal} style={styles.buttonSecondary} disabled={loading}>
                Cancel
              </button>
              <button onClick={handleSaveActivity} style={styles.buttonPrimary} disabled={loading}>
                {loading ? 'Saving...' : 'Save Activity'}
              </button>
            </div>
          </div>
        </div>
      )}

      {partnerModalDate && activities[partnerModalDate]?.partner && (
        <div style={styles.modalOverlay} onClick={() => setPartnerModalDate(null)}>
          <div style={styles.modalContent} className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {new Date(partnerModalDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </h3>
              <button onClick={() => setPartnerModalDate(null)} style={styles.closeButton}>
                <X size={24} />
              </button>
            </div>

            {(() => {
              const partner = activities[partnerModalDate].partner;
              const level = partner.sharingLevel;

              if (level === 1) {
                  return (
                    <div style={styles.teaseContainer}>
                      <div style={styles.teaseEmoji}>🔥</div>
                      <p style={styles.hint}>Something happened today. Your partner is keeping the details to themselves.</p>
                    </div>
                  );
                }

              if (level === 2) {
                return (
                  <div style={styles.teaseContainer}>
                    <div style={styles.teaseEmoji}>{partner.emojiArray.join('')}</div>
                    <p style={styles.hint}>That's all your partner's revealed for now.</p>
                  </div>
                );
              }

              // level 3 or 4
              return (
                <div>
                  <div style={styles.teaseContainer}>
                    <div style={styles.teaseEmoji}>{partner.emojiArray.join('')}</div>
                  </div>

                  {partner.rating > 0 && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Rating</label>
                      {renderRating(partner.rating, () => {})}
                    </div>
                  )}

                  {partner.description && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Description</label>
                      <p>{partner.description}</p>
                    </div>
                  )}

                  {partner.notes && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Notes</label>
                      <p>{partner.notes}</p>
                    </div>
                  )}

                  {partner.ejaculationCount > 0 && (
                    <div style={styles.indicator}>💦x{partner.ejaculationCount}</div>
                  )}

                  {partner.mediaUrl && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Media</label>
                      <div style={styles.mediaPreview}>
                        <div
                          style={{
                            filter: level === 3 ? 'blur(8px)' : 'none',
                          }}
                        >
                          {partner.mediaType === 'image' && (
                            <img src={`${API_URL}${partner.mediaUrl}`} alt="" style={styles.previewImage} />
                          )}
                          {partner.mediaType === 'video' && (
                            <video src={`${API_URL}${partner.mediaUrl}`} style={styles.previewVideo} controls={level === 4} />
                          )}
                          {partner.mediaType === 'audio' && (
                            <audio src={`${API_URL}${partner.mediaUrl}`} style={styles.previewAudio} controls={level === 4} />
                          )}
                        </div>
                        {level === 3 && (
                          <p style={styles.hint}>Your partner chose to keep the media blurred for this one.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)',
    padding: '20px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '15px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#2c3e50',
    margin: '0 0 8px 0',
  },
  userEmail: {
    fontSize: '13px',
    color: '#7f8c8d',
    margin: 0,
  },
  pointsBoard: {
    display: 'flex',
    gap: '12px',
  },
  pointsCard: {
    background: 'white',
    borderRadius: '10px',
    padding: '10px 16px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    textAlign: 'center',
    minWidth: '90px',
  },
  pointsLabel: {
    fontSize: '11px',
    color: '#7f8c8d',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  pointsTotal: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#3498db',
    margin: '2px 0',
  },
  pointsSub: {
    fontSize: '11px',
    color: '#7f8c8d',
  },
  subtitle: {
    fontSize: '14px',
    color: '#7f8c8d',
    margin: '0',
  },
  loginContainer: {
    maxWidth: '400px',
    margin: '60px auto',
    background: 'white',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  loginForm: {
    marginTop: '30px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    color: '#2c3e50',
    fontWeight: '500',
    marginBottom: '8px',
    fontSize: '14px',
  },
  hint: {
    fontSize: '12px',
    color: '#7f8c8d',
    marginTop: '4px',
    margin: '4px 0 0 0',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '2px solid #ecf0f1',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    transition: 'border-color 0.3s',
  },
  passwordWrapper: {
    position: 'relative',
  },
  eyeButton: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#7f8c8d',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '2px solid #ecf0f1',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    minHeight: '80px',
    resize: 'vertical',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    color: '#2c3e50',
    fontSize: '14px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  sharingLevelGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sharingLevelOption: {
    textAlign: 'left',
    padding: '10px 12px',
    border: '2px solid #ecf0f1',
    borderRadius: '6px',
    background: '#f9fafb',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#2c3e50',
    transition: 'all 0.2s',
  },
  sharingLevelOptionSelected: {
    borderColor: '#3498db',
    background: '#ecf7ff',
    fontWeight: '600',
  },
  teaseContainer: {
    textAlign: 'center',
    padding: '20px 0',
  },
  teaseEmoji: {
    fontSize: '48px',
    lineHeight: '1',
    marginBottom: '10px',
  },
  error: {
    color: '#e74c3c',
    fontSize: '13px',
    marginBottom: '15px',
  },
  toggleLink: {
    background: 'none',
    border: 'none',
    color: '#3498db',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: '14px',
    marginTop: '15px',
  },
  calendarWrapper: {
    maxWidth: '100%',
    margin: '0 auto',
    background: 'white',
    borderRadius: '12px',
    padding: '25px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    overflowX: 'auto',
  },
  monthHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
  },
  monthTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#2c3e50',
    margin: 0,
  },
  navButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#3498db',
    transition: 'transform 0.2s',
  },
  weekdays: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '10px',
    marginBottom: '10px',
  },
  weekday: {
    textAlign: 'center',
    fontWeight: '600',
    color: '#7f8c8d',
    fontSize: '13px',
    padding: '10px 0',
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '10px',
  },
  calendarDay: {
      aspectRatio: '1',
      borderStyle: 'solid',
      borderWidth: '2px',
      borderColor: '#ecf0f1',
      boxShadow: 'none',
      borderRadius: '10px',
      padding: '8px',
      cursor: 'pointer',
      background: '#f9fafb',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      fontSize: '12px',
      minHeight: '80px',
    },
  calendarDayActive: {
      borderColor: '#3498db',
      background: '#ecf7ff',
    },
  calendarDayToday: {
      borderColor: '#f39c12',
      borderWidth: '3px',
      boxShadow: '0 0 8px rgba(243, 156, 18, 0.3)',
    },
  emptyDay: {
    aspectRatio: '1',
  },
  dayNumber: {
    fontWeight: '600',
    color: '#2c3e50',
    fontSize: '12px',
  },
  dayContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '10px',
  },
  userActivity: {
    textAlign: 'center',
  },
  dayEmoji: {
    fontSize: '14px',
    lineHeight: '1',
  },
  indicator: {
    fontSize: '9px',
    color: '#e74c3c',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalContent: {
    background: 'white',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#2c3e50',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#7f8c8d',
    padding: 0,
  },
  emojiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '8px',
    marginBottom: '12px',
  },
  emojiOption: {
    fontSize: '24px',
    padding: '10px',
    border: '2px solid #ecf0f1',
    borderRadius: '6px',
    cursor: 'pointer',
    background: '#f9fafb',
    transition: 'all 0.3s',
  },
  emojiOptionSelected: {
    borderColor: '#3498db',
    background: '#ecf7ff',
    transform: 'scale(1.1)',
  },
  selectedEmojis: {
    fontSize: '12px',
    color: '#7f8c8d',
    marginTop: '8px',
  },
  ratingContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  starButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    transition: 'transform 0.2s',
  },
  uploadArea: {
    border: '2px dashed #3498db',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    background: '#ecf7ff',
    position: 'relative',
  },
  fileInput: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: 0,
    top: 0,
    opacity: 0,
    cursor: 'pointer',
  },
  uploadText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#3498db',
    fontSize: '14px',
    gap: '8px',
  },
  mediaPreview: {
    marginTop: '15px',
    position: 'relative',
  },
  previewImage: {
    maxWidth: '100%',
    maxHeight: '200px',
    borderRadius: '6px',
    marginBottom: '10px',
  },
  previewVideo: {
    maxWidth: '100%',
    maxHeight: '200px',
    borderRadius: '6px',
    marginBottom: '10px',
  },
  previewAudio: {
    width: '100%',
    marginBottom: '10px',
  },
  modalButtons: {
    display: 'flex',
    gap: '10px',
    marginTop: '25px',
    flexWrap: 'wrap',
  },
  buttonPrimary: {
    flex: 1,
    minWidth: '100px',
    padding: '10px 20px',
    background: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    transition: 'background 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  buttonSecondary: {
    flex: 1,
    minWidth: '100px',
    padding: '10px 20px',
    background: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    transition: 'background 0.3s',
  },
  buttonDanger: {
    flex: 1,
    minWidth: '100px',
    padding: '10px 20px',
    background: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    transition: 'background 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  buttonRemove: {
    width: '100%',
    padding: '8px',
    background: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
  },
};

export default ActivityCalendar;